// frontend/store/themeSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the available theme modes
export type ThemeMode = 'light' | 'dark' | 'blue';

interface ThemeState {
  mode: ThemeMode;
}

// Helper function to get theme from localStorage
const getStoredTheme = (): ThemeMode => {
  try {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme-mode');
      if (stored && ['light', 'dark', 'blue'].includes(stored)) {
        return stored as ThemeMode;
      }
    }
  } catch (error) {
    console.warn('Failed to load theme from localStorage:', error);
  }
  return 'dark'; // Default fallback
};

// Helper function to save theme to localStorage
const saveThemeToStorage = (theme: ThemeMode) => {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme-mode', theme);
    }
  } catch (error) {
    console.warn('Failed to save theme to localStorage:', error);
  }
};

const initialState: ThemeState = {
  mode: getStoredTheme(),
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    toggleTheme: (state) => {
      // Cycle through themes: light -> dark -> blue -> light
      const themeOrder: ThemeMode[] = ['light', 'dark', 'blue'];
      const currentIndex = themeOrder.indexOf(state.mode);
      const nextIndex = (currentIndex + 1) % themeOrder.length;
      state.mode = themeOrder[nextIndex];
      // Save to localStorage
      saveThemeToStorage(state.mode);
    },
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      // Save to localStorage
      saveThemeToStorage(state.mode);
    },
  },
});

export const { toggleTheme, setTheme } = themeSlice.actions;
export default themeSlice.reducer;
